import React, { useState, useEffect } from 'react';
import './ProjetsPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toSentenceCase } from '../utils/textUtils';
import projetService from '../services/projetService';
import opportuniteService from '../services/opportuniteService';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import useSEO from '../hooks/useSEO';

function ProjetsPage() {
  useSEO({
    title: "Projets & Campagne de Solidarité | Parrainage & Bourses de Scolarisation",
    description: "Découvrez les projets éducatifs, les équipements pédagogiques et la campagne de scolarisation gratuite des jeunes filles du GSU Les Palmiers à Togba.",
    keywords: "projets scolaires Bénin, scolarisation des filles Togba, bourses éducation Abomey-Calavi, parrainage scolaire Bénin, cagnotte solidaire GSU",
    canonicalUrl: "https://gsulespalmiers.bj/projets"
  });

  const [activeTab, setActiveTab] = useState('projets');
  const [projets, setProjets] = useState([]);
  const [opportunites, setOpportunites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [showProposerModal, setShowProposerModal] = useState(false);
  const [proposerData, setProposerData] = useState({
    organisme: '',
    contact_email: '',
    contact_telephone: '',
    type: 'stage',
    titre: '',
    description: '',
    pays: 'Bénin',
    ville: '',
    duree: '',
    montant: ''
  });
  const [submittingProposer, setSubmittingProposer] = useState(false);

  const [selectedOffrePostuler, setSelectedOffrePostuler] = useState(null);
  const [postulerData, setPostulerData] = useState({
    opportunite_titre: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    classe: '',
    motivation: ''
  });
  const [submittingPostuler, setSubmittingPostuler] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Charger les projets
      const projetsResponse = await projetService.getAll();
      setProjets(projetsResponse.data.data || projetsResponse.data || []);
      
      // Charger les opportunités (stages + bourses)
      const opportunitesResponse = await opportuniteService.getAll();
      setOpportunites(opportunitesResponse.data.data || opportunitesResponse.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContribuer = (projet) => {
    if (projet) {
      localStorage.setItem('selected_projet_donation', JSON.stringify({
        id: projet.id,
        titre: projet.titre,
        objectif_montant: projet.objectif_montant,
        montant_collecte: projet.montant_collecte
      }));
    }
    if (window.navigateTo) {
      window.navigateTo('donation', projet);
      window.scrollTo(0, 0);
    }
  };

  const handleEnSavoirPlus = (projet) => {
    setSelectedProjet(projet);
  };

  const handleOpenProposer = () => {
    setProposerData({
      organisme: '',
      contact_email: '',
      contact_telephone: '',
      type: 'stage',
      titre: '',
      description: '',
      pays: 'Bénin',
      ville: '',
      duree: '',
      montant: ''
    });
    setShowProposerModal(true);
  };

  const handleSubmitProposer = async (e) => {
    e.preventDefault();
    try {
      setSubmittingProposer(true);
      await opportuniteService.proposer(proposerData);
      alert("Votre proposition d'offre a été transmise avec succès ! Elle sera examinée par l'administration.");
      setShowProposerModal(false);
    } catch (error) {
      console.error('Erreur lors de la soumission de l\'offre:', error);
      alert('Erreur lors de l\'envoi de votre proposition. Veuillez réessayer.');
    } finally {
      setSubmittingProposer(false);
    }
  };

  const handleOpenPostuler = (offre = null) => {
    const titreOffre = offre ? offre.titre : 'Candidature Générale Stage / Bourse';
    setPostulerData({
      opportunite_titre: titreOffre,
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      classe: '',
      motivation: ''
    });
    setSelectedOffrePostuler(offre || { titre: titreOffre });
  };

  const handleSubmitPostuler = async (e) => {
    e.preventDefault();
    try {
      setSubmittingPostuler(true);
      await opportuniteService.postuler(postulerData);
      alert("Votre candidature a été transmise avec succès à l'administration du GSU Les Palmiers !");
      setSelectedOffrePostuler(null);
    } catch (error) {
      console.error('Erreur lors de la candidature:', error);
      alert('Erreur lors de la transmission de votre candidature. Veuillez réessayer.');
    } finally {
      setSubmittingPostuler(false);
    }
  };

  return (
    <div className="projets-page">
      <Header activePage="projets" />
      
      {/* Hero Section */}
      <section className="projets-hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              {toSentenceCase("Projets & Opportunités")}
            </h1>
            <p className="hero-description">
              {toSentenceCase("Participez à la construction de l'excellence éducative. Chaque contribution compte pour transformer la vie de nos élèves et bâtir l'avenir du Bénin.")}
            </p>
            <div className="hero-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => handleContribuer(null)}
              >
                {toSentenceCase("Faire un don")}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  if (window.navigateTo) {
                    window.navigateTo('partner');
                    window.scrollTo(0, 0);
                  }
                }}
              >
                {toSentenceCase("Devenir partenaire")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="tabs-section">
        <div className="container">
          <div className="tabs-nav">
            <button 
              className={`tab-btn ${activeTab === 'projets' ? 'active' : ''}`}
              onClick={() => setActiveTab('projets')}
            >
              {toSentenceCase("Projets en cours")}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'opportunites' ? 'active' : ''}`}
              onClick={() => setActiveTab('opportunites')}
            >
              {toSentenceCase("Opportunités")}
            </button>
          </div>
        </div>
      </section>

      {/* Projets Content */}
      {activeTab === 'projets' && (
        <section className="projets-content">
          <div className="container">
            {loading ? (
              <div className="loading">Chargement des projets...</div>
            ) : projets.length === 0 ? (
              <div className="no-data" style={{ textAlign: 'center', padding: '4rem 1.5rem', background: '#ffffff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', margin: '2rem 0' }}>
                <div style={{ margin: '0 auto 1.25rem', width: 64, height: 64, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Aucun projet pour le moment</h3>
                <p style={{ color: '#64748b', fontSize: '0.95rem', maxWidth: 460, margin: '0 auto' }}>
                  Les nouveaux projets institutionnels et cagnottes solidaires du lycée seront publiés ici très prochainement.
                </p>
              </div>
            ) : (
              <div className="projets-grid">
                {projets.map((projet) => {
                  const progression = projet.objectif_montant > 0 
                    ? Math.min(100, Math.round((projet.montant_collecte / projet.objectif_montant) * 100))
                    : 0;
                  
                  return (
                    <div key={projet.id} className="projet-card">
                      {projet.statut === 'urgent' && <div className="urgent-badge">Urgent</div>}
                      
                      <div className="projet-image">
                        {projet.image ? (
                          <img 
                            src={getImageUrl(projet.image, '/images/logo.svg')} 
                            alt={projet.titre}
                            onError={(e) => handleImageError(e, '/images/logo.svg')}
                          />
                        ) : (
                          <img src="/images/logo.svg" alt={projet.titre} />
                        )}
                        <div className="projet-category">{projet.type === 'projet' ? 'Projet' : 'Cagnotte'}</div>
                      </div>
                      
                      <div className="projet-content">
                        <h3 className="projet-title">{toSentenceCase(projet.titre)}</h3>
                        <p className="projet-description">{toSentenceCase(projet.description)}</p>
                        
                        <div className="projet-stats">
                          <div className="stat-row">
                            <div className="stat-item">
                              <span className="stat-label">Objectif</span>
                              <span className="stat-value">
                                {projet.objectif_montant 
                                  ? `${(projet.objectif_montant / 1000000).toFixed(1)}M FCFA`
                                  : 'N/A'
                                }
                              </span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-label">Collecté</span>
                              <span className="stat-value">
                                {(projet.montant_collecte / 1000000).toFixed(1)}M FCFA
                              </span>
                            </div>
                          </div>
                          
                          {projet.objectif_montant > 0 && (
                            <div className="progress-section">
                              <div className="progress-header">
                                <span>Financement</span>
                                <span>{progression}%</span>
                              </div>
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${progression}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {projet.beneficiaires && Array.isArray(projet.beneficiaires) && projet.beneficiaires.length > 0 && (
                            <div className="beneficiaires-section">
                              <div className="progress-header">
                                <span>Bénéficiaires</span>
                                <span>{projet.beneficiaires.length}</span>
                              </div>
                              <div className="beneficiaires-list">
                                {projet.beneficiaires.slice(0, 3).map((benef, idx) => (
                                  <span key={idx} className="beneficiaire-tag">{benef}</span>
                                ))}
                                {projet.beneficiaires.length > 3 && (
                                  <span className="beneficiaire-tag">+{projet.beneficiaires.length - 3}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="projet-actions">
                          <button 
                            className="btn-contribute"
                            onClick={() => handleContribuer(projet)}
                          >
                            {toSentenceCase("Contribuer")}
                          </button>
                          <button 
                            className="btn-details"
                            onClick={() => handleEnSavoirPlus(projet)}
                          >
                            {toSentenceCase("En savoir plus")}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Opportunités Content */}
      {activeTab === 'opportunites' && (
        <section className="opportunites-content">
          <div className="container">
            <div className="opportunites-header">
              <h2>{toSentenceCase("Stages & Bourses")}</h2>
              <p>{toSentenceCase("Découvrez les opportunités offertes par nos partenaires")}</p>
            </div>

            {loading ? (
              <div className="loading">Chargement des opportunités...</div>
            ) : opportunites.length === 0 ? (
              <div className="no-data">
                <p>Aucune opportunité disponible pour le moment.</p>
              </div>
            ) : (
              <div className="section-block">
                <h3 className="section-title">
                  <span className="icon">🏢</span>
                  {toSentenceCase("Offres disponibles")}
                </h3>
                
                <div className="offres-grid">
                  {opportunites.map((offre) => (
                    <div key={offre.id} className="offre-card">
                      <div className="offre-header">
                        <h4>{toSentenceCase(offre.titre)}</h4>
                        <span className={`offre-type ${offre.type ? offre.type.toLowerCase() : 'stage'}`}>
                          {offre.type === 'stage' ? 'Stage' : 'Bourse'}
                        </span>
                      </div>
                      
                      <div className="offre-details">
                        <div className="detail-item">
                          <span className="label">Organisme :</span>
                          <span className="value">{toSentenceCase(offre.organisme)}</span>
                        </div>
                        
                        {offre.pays && (
                          <div className="detail-item">
                            <span className="label">Pays :</span>
                            <span className="value">{toSentenceCase(offre.pays)}</span>
                          </div>
                        )}
                        
                        {offre.duree && (
                          <div className="detail-item">
                            <span className="label">Durée :</span>
                            <span className="value">{offre.duree}</span>
                          </div>
                        )}
                        
                        {offre.montant && (
                          <div className="detail-item">
                            <span className="label">Montant :</span>
                            <span className="value">{offre.montant}</span>
                          </div>
                        )}
                        
                        {offre.date_limite && (
                          <div className="detail-item">
                            <span className="label">Date limite :</span>
                            <span className="value">
                              {new Date(offre.date_limite).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <button 
                        className="btn-postuler"
                        onClick={() => handleOpenPostuler(offre)}
                      >
                        {toSentenceCase("Postuler")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Sections */}
            <div className="cta-sections">
              <div className="cta-card entreprise">
                <div className="cta-content">
                  <h3>{toSentenceCase("Vous êtes une entreprise ?")}</h3>
                  <p>{toSentenceCase("Proposez un stage ou une bourse à nos étudiants talentueux")}</p>
                  <button 
                    className="btn-cta"
                    onClick={handleOpenProposer}
                  >
                    {toSentenceCase("Proposer une offre")}
                  </button>
                </div>
              </div>
              
              <div className="cta-card etudiant">
                <div className="cta-content">
                  <h3>{toSentenceCase("Vous êtes étudiant ?")}</h3>
                  <p>{toSentenceCase("Postulez directement en ligne pour un stage ou une bourse")}</p>
                  <button 
                    className="btn-cta"
                    onClick={() => handleOpenPostuler(null)}
                  >
                    {toSentenceCase("Postuler maintenant")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MODALE 1: Détail d'un projet ("En savoir plus") */}
      {selectedProjet && (
        <div className="modal-overlay" onClick={() => setSelectedProjet(null)}>
          <div className="modal-container projet-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="badge-tag">{selectedProjet.type === 'projet' ? 'Projet École' : 'Cagnotte Solidaire'}</span>
              <h2>{toSentenceCase(selectedProjet.titre)}</h2>
              <button className="modal-close" onClick={() => setSelectedProjet(null)}>✕</button>
            </div>
            <div className="modal-body">
              {selectedProjet.image && (
                <div className="modal-projet-img-wrapper">
                  <img 
                    src={getImageUrl(selectedProjet.image, '/images/logo.svg')} 
                    alt={selectedProjet.titre}
                    className="modal-projet-img"
                    onError={(e) => handleImageError(e, '/images/logo.svg')}
                  />
                </div>
              )}
              
              <div className="projet-full-desc">
                <h4>Description du Projet</h4>
                <p>{selectedProjet.description}</p>
              </div>

              {selectedProjet.contenu && (
                <div className="projet-extended-content">
                  <h4>Détails Complémentaires</h4>
                  <p>{selectedProjet.contenu}</p>
                </div>
              )}

              <div className="modal-stats-box">
                <div className="stat-row">
                  <div>
                    <span className="stat-label">Objectif visé :</span>
                    <strong className="stat-val">{selectedProjet.objectif_montant ? `${Number(selectedProjet.objectif_montant).toLocaleString('fr-FR')} FCFA` : 'Non défini'}</strong>
                  </div>
                  <div>
                    <span className="stat-label">Total Collecté :</span>
                    <strong className="stat-val highlight-text">{selectedProjet.montant_collecte ? `${Number(selectedProjet.montant_collecte).toLocaleString('fr-FR')} FCFA` : '0 FCFA'}</strong>
                  </div>
                </div>
                {selectedProjet.objectif_montant > 0 && (
                  <div className="modal-progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${Math.min(100, Math.round((selectedProjet.montant_collecte / selectedProjet.objectif_montant) * 100))}%` }}
                    ></div>
                  </div>
                )}
              </div>

              {selectedProjet.beneficiaires && Array.isArray(selectedProjet.beneficiaires) && selectedProjet.beneficiaires.length > 0 && (
                <div className="modal-section">
                  <h4>Bénéficiaires du projet</h4>
                  <div className="tags-container">
                    {selectedProjet.beneficiaires.map((b, idx) => (
                      <span key={idx} className="tag-chip">{b}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedProjet.partenaires && Array.isArray(selectedProjet.partenaires) && selectedProjet.partenaires.length > 0 && (
                <div className="modal-section">
                  <h4>Partenaires associés</h4>
                  <div className="tags-container">
                    {selectedProjet.partenaires.map((p, idx) => (
                      <span key={idx} className="tag-chip partner">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedProjet(null)}>Fermer</button>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  const p = selectedProjet;
                  setSelectedProjet(null);
                  handleContribuer(p);
                }}
              >
                Contribuer à ce projet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE 2: Proposer une Offre (Entreprises) */}
      {showProposerModal && (
        <div className="modal-overlay" onClick={() => setShowProposerModal(false)}>
          <div className="modal-container form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏢 Proposer une offre (Stage / Bourse)</h2>
              <button className="modal-close" onClick={() => setShowProposerModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitProposer} className="modal-body modal-form">
              <p className="modal-intro">Proposez des bourses ou opportunités de stage aux élèves du GSU Les Palmiers.</p>
              
              <div className="form-group">
                <label>Nom de l'Entreprise / Organisme *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="Ex: GCB SARL, MTN, Moov, Banque..." 
                  value={proposerData.organisme}
                  onChange={(e) => setProposerData({...proposerData, organisme: e.target.value})}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email de contact *</label>
                  <input 
                    type="email" 
                    required 
                    placeholder="rh@entreprise.com" 
                    value={proposerData.contact_email}
                    onChange={(e) => setProposerData({...proposerData, contact_email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone de contact</label>
                  <input 
                    type="tel" 
                    placeholder="+229 90 00 00 00" 
                    value={proposerData.contact_telephone}
                    onChange={(e) => setProposerData({...proposerData, contact_telephone: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type d'offre *</label>
                  <select 
                    value={proposerData.type}
                    onChange={(e) => setProposerData({...proposerData, type: e.target.value})}
                  >
                    <option value="stage">Stage professionnel / académique</option>
                    <option value="bourse">Bourse d'études / Soutien financier</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Titre de l'offre *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="Ex: Stage Assistant Informatique / Bourse d'Excellence" 
                    value={proposerData.titre}
                    onChange={(e) => setProposerData({...proposerData, titre: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Pays</label>
                  <input 
                    type="text" 
                    placeholder="Bénin" 
                    value={proposerData.pays}
                    onChange={(e) => setProposerData({...proposerData, pays: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input 
                    type="text" 
                    placeholder="Cotonou / Abomey-Calavi" 
                    value={proposerData.ville}
                    onChange={(e) => setProposerData({...proposerData, ville: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description détaillée & Critères *</label>
                <textarea 
                  rows="4" 
                  required 
                  placeholder="Présentez l'offre, les missions et les pré-requis demandés..." 
                  value={proposerData.description}
                  onChange={(e) => setProposerData({...proposerData, description: e.target.value})}
                ></textarea>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowProposerModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={submittingProposer}>
                  {submittingProposer ? 'Soumission...' : 'Envoyer l\'offre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODALE 3: Postuler à une Opportunité (Élèves) */}
      {selectedOffrePostuler && (
        <div className="modal-overlay" onClick={() => setSelectedOffrePostuler(null)}>
          <div className="modal-container form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎓 Candidature : {selectedOffrePostuler.titre}</h2>
              <button className="modal-close" onClick={() => setSelectedOffrePostuler(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmitPostuler} className="modal-body modal-form">
              <p className="modal-intro">Remplissez vos coordonnées et présentez votre motivation pour postuler à cette opportunité.</p>

              <div className="form-group">
                <label>Offre visée</label>
                <input 
                  type="text" 
                  readOnly 
                  className="input-readonly"
                  value={postulerData.opportunite_titre}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="Votre nom" 
                    value={postulerData.nom}
                    onChange={(e) => setPostulerData({...postulerData, nom: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Prénom *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="Votre prénom" 
                    value={postulerData.prenom}
                    onChange={(e) => setPostulerData({...postulerData, prenom: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input 
                    type="email" 
                    required 
                    placeholder="votre.email@student.lespalmiers.bj" 
                    value={postulerData.email}
                    onChange={(e) => setPostulerData({...postulerData, email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input 
                    type="tel" 
                    placeholder="+229 90 00 00 00" 
                    value={postulerData.telephone}
                    onChange={(e) => setPostulerData({...postulerData, telephone: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Classe / Niveau d'étude *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="Ex: Seconde A, Terminale C, Licence 1..." 
                  value={postulerData.classe}
                  onChange={(e) => setPostulerData({...postulerData, classe: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Message de motivation *</label>
                <textarea 
                  rows="4" 
                  required 
                  placeholder="Expliquez brièvement votre motivation et votre projet d'étude..." 
                  value={postulerData.motivation}
                  onChange={(e) => setPostulerData({...postulerData, motivation: e.target.value})}
                ></textarea>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedOffrePostuler(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={submittingPostuler}>
                  {submittingPostuler ? 'Envoi...' : 'Transmettre ma candidature'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default ProjetsPage;
